import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, Trash2, ArrowRight, ShoppingBag, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CloudImage } from "@/components/CloudImage";
import { GradeBadge } from "@/components/GradeBadge";
import { Reveal, EASE_OUT } from "@/components/motion/Reveal";
import { AnimatedPrice } from "@/components/motion/AnimatedPrice";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/lib/format";
import { CLD_THUMB } from "@/lib/variants";

export default function Cart() {
  const navigate = useNavigate();
  const { items, updateQty, removeItem, subtotal, count } = useCart();

  if (items.length === 0) {
    return (
      <div className="container-page section" data-testid="cart-page">
        <Reveal className="max-w-2xl">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-border">
            <ShoppingBag className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
          </span>
          <div className="eyebrow mt-8">Carrello</div>
          <h1 className="display-xl mt-4 text-foreground">La tua vetrina è ancora vuota.</h1>
          <p className="mt-5 max-w-md text-lg text-muted-foreground">Scopri i pezzi nuovi e ricondizionati, verificati su 32 punti di controllo.</p>
          <Button asChild variant="lime" size="lg" className="mt-10" data-testid="cart-empty-cta">
            <Link to="/catalogo">Vai al catalogo <ArrowRight strokeWidth={2} /></Link>
          </Button>
        </Reveal>
      </div>
    );
  }

  return (
    <div className="container-page pb-24 pt-10 sm:pt-14" data-testid="cart-page">
      <Reveal className="flex flex-col gap-4 border-b border-border pb-10 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="eyebrow">Carrello</div>
          <h1 className="display-xl mt-4 text-foreground">I tuoi pezzi</h1>
        </div>
        <span className="font-mono text-[11px] uppercase tracking-mono text-muted-foreground" data-testid="cart-items-count">{String(count).padStart(2, "0")} articoli</span>
      </Reveal>

      <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
        <div className="lg:col-span-8">
          <ul className="divide-y divide-border border-b border-border" data-testid="cart-items-list">
            <AnimatePresence initial={false}>
              {items.map((item) => (
                <motion.li
                  key={item._lineId}
                  layout
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.3, ease: EASE_OUT }}
                  className="flex gap-5 py-6 sm:gap-7"
                  data-testid="cart-item"
                >
                  <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-xl border border-border bg-surface sm:h-28 sm:w-28">
                    {item.image ? (
                      <CloudImage url={item.image} alt={item.name} transform={CLD_THUMB} className="absolute inset-0 h-full w-full object-contain" />
                    ) : (
                      <div className="placeholder-spot absolute inset-0" aria-hidden="true" />
                    )}
                  </div>
                  <div className="flex flex-1 flex-col">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="mono-label text-muted-foreground">{item.brand}</div>
                        <Link to={`/prodotto/${item.slug}`} className="mt-1 block font-display text-lg font-semibold tracking-tightest text-foreground transition-colors hover:text-lime" data-testid="cart-item-name">
                          {item.name}
                        </Link>
                        <div className="mt-2"><GradeBadge grade={item.grade} /></div>
                      </div>
                      <button
                        onClick={() => removeItem(item._lineId)}
                        className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-muted-foreground transition-[border-color,color] duration-200 hover:border-destructive hover:text-destructive"
                        aria-label="Rimuovi"
                        data-testid="cart-remove-item"
                      >
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                      </button>
                    </div>
                    <div className="mt-auto flex items-center justify-between pt-4">
                      <div className="flex items-center rounded-full border border-border" data-testid="cart-qty-control">
                        <button className="flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40" onClick={() => updateQty(item._lineId, item.quantity - 1)} disabled={item.quantity <= 1} aria-label="Diminuisci" data-testid="cart-qty-decrease"><Minus className="h-3.5 w-3.5" /></button>
                        <span className="w-8 text-center font-mono text-sm" data-testid="cart-item-qty">{item.quantity}</span>
                        <button className="flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground" onClick={() => updateQty(item._lineId, item.quantity + 1)} aria-label="Aumenta" data-testid="cart-qty-increase"><Plus className="h-3.5 w-3.5" /></button>
                      </div>
                      <div className="text-right">
                        <div className="font-mono text-lg font-medium text-foreground" data-testid="cart-item-line-total">{formatPrice(item.unit_price * item.quantity)}</div>
                        {item.quantity > 1 && <div className="font-mono text-[10.5px] text-muted-foreground">{formatPrice(item.unit_price)} cad.</div>}
                      </div>
                    </div>
                  </div>
                </motion.li>
              ))}
            </AnimatePresence>
          </ul>
          <Link to="/catalogo" className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-mono text-muted-foreground transition-colors hover:text-foreground" data-testid="cart-continue-shopping">
            Continua lo shopping <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        {/* Riepilogo */}
        <aside className="lg:col-span-4">
          <Reveal className="rounded border border-border bg-card p-6 lg:sticky lg:top-[100px]" data-testid="cart-summary">
            <div className="mono-label text-muted-foreground">Riepilogo</div>
            <dl className="mt-5 space-y-3 text-sm">
              <div className="flex justify-between"><dt className="text-muted-foreground">Subtotale</dt><dd className="font-mono">{formatPrice(subtotal)}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Spedizione</dt><dd className="font-mono text-[11px] uppercase tracking-mono text-lime">Gratuita</dd></div>
            </dl>
            <div className="mt-5 flex items-end justify-between border-t border-border pt-5">
              <span className="mono-label text-muted-foreground">Totale</span>
              <AnimatedPrice value={subtotal} className="text-2xl font-medium text-foreground" data-testid="cart-total" />
            </div>
            <Button variant="lime" size="lg" className="mt-6 w-full justify-between" onClick={() => navigate("/checkout")} data-testid="cart-checkout-button">
              Vai al checkout <ArrowRight strokeWidth={2} />
            </Button>
            <p className="mt-5 flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-mono text-muted-foreground" data-testid="cart-trust-line">
              <ShieldCheck className="h-3.5 w-3.5 text-lime" strokeWidth={2} /> Pagamenti sicuri · Garanzia 12 mesi · Reso 14 giorni
            </p>
          </Reveal>
        </aside>
      </div>
    </div>
  );
}
